import { get } from 'lodash';

import { RECENT_SEARCH, SALE_SUGGESTION_TYPE } from './site_search';
import { Paths } from '../../url_helpers';
import Suggestion from './suggestion';

import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { shouldClientSideResolve } from '@reverbdotcom/commons/src/components/core_link';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';

import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

import {
  reverb_search_CompletionType as CompletionType,
} from '@reverbdotcom/commons/src/gql/graphql';
import { parseURL } from '@reverbdotcom/commons/src/link_parser';

const SELECT_INTERACTION = 'select';
const SUBMIT_INTERACTION = 'submit';

/**
 * A v7 `navigate()` function (from `useNavigate()`), bound to whatever
 * `<RouterProvider>` this search UI happens to be rendered in. Since
 * `ReverbSiteSearchController` is a static class (not a component), it can't
 * call hooks itself -- `navigate` is threaded in from `ReverbSiteSearch`,
 * the one place in this tree that can call `useNavigate()`. It's optional so
 * unit tests exercising this class directly don't need a Router, and so
 * callers rendered outside a Router gracefully fall back to a full page load.
 */
type NavigateFn = (url: string) => void;

interface ISearchInteractionEvent {
  user: IUser;
  position?: number;
  entityId?: number;
  interactionType?: string;
  context: {
    fullTextQuery?: string;
    type?: string;
    scope?: string;
    suggestions?: string[];
    query?: string;
  };
}

export default class ReverbSiteSearchController {
  static navigateWindow(href: string): void {
    window.location.href = href;
  }

  static async redirectTo(url: string, trackingParams: ISearchInteractionEvent, navigate?: NavigateFn) {
    // Only `pathname` is actually consulted by `shouldClientSideResolve`
    // (via `getRouteSettings` -> `matchRoutes`), so a plain object built from
    // `window.location` is sufficient here -- no need for a full v3 history
    // `Location` object like `RouterHistory.getCurrentLocation()` used to
    // provide.
    const currentLocation = { pathname: window.location.pathname };
    const clientSide = await shouldClientSideResolve(parseURL(url), currentLocation);

    trackEvent({
      eventName: MParticleEventName.SiteSearch,
      position: trackingParams.position,
      suggestionId: String(trackingParams.entityId || ''),
      suggestionType: trackingParams.context.type,
      suggestionQuery: trackingParams.context.query,
      suggestionCount: get(trackingParams, 'context.suggestions.length', 0),
      interactionType: trackingParams.interactionType,
      targetUrl: url,
      experiments: formatExperimentsForEventAttributes(trackingParams.user, [
        // experiments.NAME  #Add experiments in this array
      ]),
    });

    if (clientSide && navigate) {
      navigate(url);
    } else {
      ReverbSiteSearchController.navigateWindow(url);
    }
  }

  static selectSuggestion(
    user: IUser,
    suggestion: Suggestion,
    suggestions: Suggestion[],
    lastTypedValue = '',
    navigate?: NavigateFn,
  ): void {
    if (suggestion.type === RECENT_SEARCH) {
      ReverbSiteSearchController.submitRecentQuery(user, suggestion, suggestions, lastTypedValue, navigate);
      return;
    }

    const trackingEvent: ISearchInteractionEvent = {
      user,
      interactionType: SELECT_INTERACTION,
      position: suggestion.position,
      entityId: get(suggestion, 'completion.id'),
      context: {
        fullTextQuery: suggestion.value,
        type: suggestion.type,
        query: lastTypedValue,
        scope: suggestion.context,
        suggestions: ReverbSiteSearchController.suggestionValues(suggestions),
      },
    };

    if (CompletionType[suggestion.type] || suggestion.type === SALE_SUGGESTION_TYPE) {
      ReverbSiteSearchController.redirectTo(suggestion.url, trackingEvent, navigate);
      return;
    }

    const uri = new URL(suggestion.url);
    const hostlessUrl = suggestion.url.replace(uri.origin, '');

    ReverbSiteSearchController.redirectTo(hostlessUrl, trackingEvent, navigate);
  }

  private static submitRecentQuery(
    user: IUser,
    suggestion: Suggestion,
    suggestions: Suggestion[],
    lastTypedValue = '',
    navigate?: NavigateFn,
  ): void {
    const query = suggestion.value;
    const path = Paths.marketplace.expand({ query });

    const trackingEvent = {
      user,
      interactionType: SELECT_INTERACTION,
      position: suggestion.position,
      context: {
        fullTextQuery: query,
        type: RECENT_SEARCH,
        query: lastTypedValue,
        suggestions: ReverbSiteSearchController.suggestionValues(suggestions),
      },
    };

    ReverbSiteSearchController.redirectTo(path, trackingEvent, navigate);
  }

  static submitQuery(
    user: IUser,
    query: string,
    suggestions: Suggestion[],
    lastTypedValue = '',
    navigate?: NavigateFn,
  ): void {
    const queryParams = { query: query };
    const path = Paths.marketplace.expand(queryParams);
    const trackingEvent = {
      user,
      interactionType: SUBMIT_INTERACTION,
      context: {
        fullTextQuery: query,
        query: lastTypedValue,
        suggestions: ReverbSiteSearchController.suggestionValues(suggestions),
      },
    };

    ReverbSiteSearchController.redirectTo(path, trackingEvent, navigate);
  }

  private static suggestionValues(suggestions: Suggestion[]): string[] {
    return suggestions ? suggestions.map(s => s.value) : [];
  }
}
